import React, { useState, useEffect } from 'react';
import { Shield, Plus, Trash2, Edit2, CheckCircle, AlertCircle, Copy, Zap, Columns } from 'lucide-react';
import storageService from '../../services/storageService';

const ValidationRules = () => {
  const [rules, setRules] = useState([]);
  const [entities, setEntities] = useState([]);
  const [customRuleTypes, setCustomRuleTypes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    entityId: '',
    fieldId: ''
  });

  const [selectedRuleType, setSelectedRuleType] = useState('');
  const [ruleCards, setRuleCards] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  useEffect(() => {
    loadRules();
    loadEntities();
    loadCustomRuleTypes();
  }, []);

  const loadRules = () => {
    const data = storageService.getValidationRules();
    setRules(data);
  };

  const loadEntities = () => {
    const data = storageService.getDataEntities();
    setEntities(data);
  };

  const loadCustomRuleTypes = () => {
    const data = storageService.getRuleTypes();
    setCustomRuleTypes(data);
  };

  // Rule templates for common validations
  const ruleTemplates = {
    email: {
      name: 'Email format',
      type: 'regex',
      config: {
        pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
        message: 'Email không đúng định dạng'
      }
    },
    phone: {
      name: 'Số điện thoại VN',
      type: 'regex',
      config: {
        pattern: '^(\\+84|0)[0-9]{9,10}$',
        message: 'Số điện thoại không hợp lệ'
      }
    },
    cccd: {
      name: 'CMND/CCCD',
      type: 'regex',
      config: {
        pattern: '^[0-9]{9,12}$',
        message: 'CMND/CCCD phải có 9-12 chữ số'
      }
    },
    positiveNumber: {
      name: 'Số dương',
      type: 'range',
      config: {
        min: 0.01,
        message: 'Giá trị phải lớn hơn 0'
      }
    },
    age18Plus: {
      name: 'Tuổi >= 18',
      type: 'custom',
      config: {
        code: `
const dob = new Date(value);
const today = new Date();
const age = today.getFullYear() - dob.getFullYear();
return age >= 18;
        `.trim(),
        message: 'Phải từ 18 tuổi trở lên'
      }
    }
  };

  const getRuleTypeLabel = (type) => {
    const labels = {
      required: 'Bắt buộc',
      dataType: 'Kiểu dữ liệu',
      range: 'Khoảng giá trị',
      regex: 'Định dạng (Regex)',
      length: 'Độ dài',
      custom: 'Tùy chỉnh (Code)'
    };
    return labels[type] || type;
  };

  const getRuleTypeBadge = (type) => {
    const badges = {
      required: 'badge-danger',
      dataType: 'badge-info',
      range: 'badge-warning',
      regex: 'badge-success',
      length: 'badge-info',
      custom: 'badge-gray'
    };
    return badges[type] || 'badge-gray';
  };

  const handleOpenModal = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setFormData({
        entityId: rule.entityId,
        fieldId: rule.fieldId
      });
      // When editing, load existing rule as a card
      setRuleCards([{
        id: 'edit-0',
        ruleType: rule.ruleType,
        ruleName: rule.ruleName,
        config: rule.config,
        enabled: rule.enabled
      }]);
    } else {
      setEditingRule(null);
      setFormData({
        entityId: '',
        fieldId: ''
      });
      setRuleCards([]);
    }
    setSelectedTemplate('');
    setSelectedRuleType('');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingRule(null);
    setSelectedTemplate('');
    setSelectedRuleType('');
    setRuleCards([]);
  };

  const handleTemplateSelect = (templateKey) => {
    if (!templateKey) {
      setSelectedTemplate('');
      return;
    }

    const template = ruleTemplates[templateKey];
    // Add template as a new card
    const newCard = {
      id: `card-${Date.now()}`,
      ruleType: template.type,
      ruleName: template.name,
      config: { ...template.config },
      enabled: true
    };
    setRuleCards([...ruleCards, newCard]);
    setSelectedTemplate('');
  };

  const handleAddRuleCard = () => {
    if (!selectedRuleType) return;
    
    const newCard = {
      id: `card-${Date.now()}`,
      ruleType: selectedRuleType,
      ruleName: '',
      config: {},
      enabled: true
    };
    setRuleCards([...ruleCards, newCard]);
    setSelectedRuleType('');
  };

  const handleRemoveRuleCard = (cardId) => {
    setRuleCards(ruleCards.filter(card => card.id !== cardId));
  };

  const handleUpdateRuleCard = (cardId, updates) => {
    setRuleCards(ruleCards.map(card => 
      card.id === cardId ? { ...card, ...updates } : card
    ));
  };

  const handleSave = () => {
    const entity = entities.find(e => e.id === formData.entityId);
    const field = entity?.fields.find(f => f.id === formData.fieldId);

    // When editing, update the single rule
    if (editingRule && ruleCards.length === 1) {
      const card = ruleCards[0];
      const rule = {
        id: editingRule.id,
        entityId: formData.entityId,
        fieldId: formData.fieldId,
        entityName: entity?.name,
        fieldName: field?.name,
        ruleName: card.ruleName,
        ruleType: card.ruleType,
        config: card.config,
        enabled: card.enabled,
        createdAt: editingRule.createdAt,
        updatedAt: new Date().toISOString()
      };
      storageService.saveValidationRule(rule);
    } else {
      // When creating new, save each card as a separate rule
      ruleCards.forEach(card => {
        const rule = {
          id: `rule${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          entityId: formData.entityId,
          fieldId: formData.fieldId,
          entityName: entity?.name,
          fieldName: field?.name,
          ruleName: card.ruleName || `${getRuleTypeLabel(card.ruleType)} - ${field?.name}`,
          ruleType: card.ruleType,
          config: card.config,
          enabled: card.enabled,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        storageService.saveValidationRule(rule);
      });
    }
    
    loadRules();
    handleCloseModal();
  };

  const handleDelete = (id) => {
    if (window.confirm('Bạn có chắc muốn xóa quy tắc này?')) {
      storageService.deleteValidationRule(id);
      loadRules();
    }
  };

  const handleToggleEnabled = (rule) => {
    const updated = { ...rule, enabled: !rule.enabled };
    storageService.saveValidationRule(updated);
    loadRules();
  };

  const handleDuplicate = (rule) => {
    const newRule = {
      ...rule,
      id: `rule${Date.now()}`,
      ruleName: `${rule.ruleName} (Copy)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    storageService.saveValidationRule(newRule);
    loadRules();
  };

  const selectedEntity = entities.find(e => e.id === formData.entityId);
  const selectedField = selectedEntity?.fields.find(f => f.id === formData.fieldId);
  
  // Filter existing rules for the selected field
  const existingFieldRules = formData.fieldId 
    ? rules.filter(r => r.entityId === formData.entityId && r.fieldId === formData.fieldId)
    : [];

  const renderRuleCardConfig = (card) => {
    const updateCardConfig = (updates) => {
      handleUpdateRuleCard(card.id, { config: { ...card.config, ...updates } });
    };

    const updateCardField = (field, value) => {
      handleUpdateRuleCard(card.id, { [field]: value });
    };

    switch (card.ruleType) {
      case 'required':
        return (
          <div className="form-group">
            <label>Thông báo lỗi</label>
            <input
              type="text"
              value={card.config.message || ''}
              onChange={(e) => updateCardConfig({ message: e.target.value })}
              placeholder="VD: Trường này là bắt buộc"
            />
          </div>
        );

      case 'dataType':
        return (
          <>
            <div className="form-group">
              <label>Kiểu dữ liệu <span className="required">*</span></label>
              <select
                value={card.config.type || ''}
                onChange={(e) => updateCardConfig({ type: e.target.value })}
              >
                <option value="">-- Chọn kiểu dữ liệu --</option>
                <option value="string">String (Chuỗi ký tự)</option>
                <option value="number">Number (Số)</option>
                <option value="date">Date (Ngày tháng)</option>
                <option value="boolean">Boolean (True/False)</option>
                <option value="email">Email</option>
                <option value="url">URL</option>
              </select>
              <small>Kiểm tra giá trị có đúng kiểu dữ liệu không</small>
            </div>
            <div className="form-group">
              <label>Thông báo lỗi</label>
              <input
                type="text"
                value={card.config.message || ''}
                onChange={(e) => updateCardConfig({ message: e.target.value })}
                placeholder="VD: Giá trị phải là kiểu số"
              />
            </div>
          </>
        );

      case 'range':
        return (
          <>
            <div className="form-row">
              <div className="form-group">
                <label>Giá trị tối thiểu</label>
                <input
                  type="number"
                  value={card.config.min || ''}
                  onChange={(e) => updateCardConfig({ min: parseFloat(e.target.value) || undefined })}
                  placeholder="VD: 0"
                />
                <small>Bỏ trống nếu không giới hạn dưới</small>
              </div>

              <div className="form-group">
                <label>Giá trị tối đa</label>
                <input
                  type="number"
                  value={card.config.max || ''}
                  onChange={(e) => updateCardConfig({ max: parseFloat(e.target.value) || undefined })}
                  placeholder="VD: 1000000"
                />
                <small>Bỏ trống nếu không giới hạn trên</small>
              </div>
            </div>

            <div className="form-group">
              <label>Thông báo lỗi</label>
              <input
                type="text"
                value={card.config.message || ''}
                onChange={(e) => updateCardConfig({ message: e.target.value })}
                placeholder="VD: Giá trị phải từ 0 đến 1,000,000"
              />
            </div>
          </>
        );

      case 'regex':
        return (
          <>
            <div className="form-group">
              <label>Biểu thức chính quy (Pattern) <span className="required">*</span></label>
              <input
                type="text"
                value={card.config.pattern || ''}
                onChange={(e) => updateCardConfig({ pattern: e.target.value })}
                placeholder="VD: ^[0-9]{16}$ (16 chữ số)"
                style={{fontFamily: 'monospace'}}
              />
              <small>
                Ví dụ: <code>^[0-9]+$</code> (chỉ số), <code>^[A-Z][a-z]+$</code> (chữ hoa đầu)
              </small>
            </div>

            <div className="form-group">
              <label>Chuỗi test (để kiểm tra pattern)</label>
              <input
                type="text"
                placeholder="Nhập giá trị test"
                onBlur={(e) => {
                  if (card.config.pattern && e.target.value) {
                    try {
                      const regex = new RegExp(card.config.pattern);
                      const isValid = regex.test(e.target.value);
                      alert(isValid ? '✓ Hợp lệ!' : '✗ Không hợp lệ');
                    } catch (err) {
                      alert('Pattern không hợp lệ: ' + err.message);
                    }
                  }
                }}
              />
              <small>Nhập và blur để test pattern</small>
            </div>

            <div className="form-group">
              <label>Thông báo lỗi</label>
              <input
                type="text"
                value={card.config.message || ''}
                onChange={(e) => updateCardConfig({ message: e.target.value })}
                placeholder="VD: Định dạng không hợp lệ"
              />
            </div>
          </>
        );

      case 'length':
        return (
          <>
            <div className="form-row">
              <div className="form-group">
                <label>Độ dài tối thiểu</label>
                <input
                  type="number"
                  value={card.config.min || ''}
                  onChange={(e) => updateCardConfig({ min: parseInt(e.target.value) || undefined })}
                  placeholder="VD: 3"
                />
              </div>

              <div className="form-group">
                <label>Độ dài tối đa</label>
                <input
                  type="number"
                  value={card.config.max || ''}
                  onChange={(e) => updateCardConfig({ max: parseInt(e.target.value) || undefined })}
                  placeholder="VD: 100"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Thông báo lỗi</label>
              <input
                type="text"
                value={card.config.message || ''}
                onChange={(e) => updateCardConfig({ message: e.target.value })}
                placeholder="VD: Độ dài phải từ 3 đến 100 ký tự"
              />
            </div>
          </>
        );

      case 'custom':
        return (
          <>
            <div className="form-group">
              <label>JavaScript Code <span className="required">*</span></label>
              <textarea
                value={card.config.code || ''}
                onChange={(e) => updateCardConfig({ code: e.target.value })}
                placeholder="VD: return value > 0 && value < 100;"
                rows={6}
                style={{fontFamily: 'monospace', fontSize: '0.875rem'}}
              />
              <small>
                Code phải return <code>true</code> (hợp lệ) hoặc <code>false</code> (lỗi).
                Biến <code>value</code> chứa giá trị cần validate.
              </small>
            </div>

            <div className="form-group">
              <label>Thông báo lỗi</label>
              <input
                type="text"
                value={card.config.message || ''}
                onChange={(e) => updateCardConfig({ message: e.target.value })}
                placeholder="VD: Giá trị không hợp lệ"
              />
            </div>
          </>
        );

      default:
        // Check if it's a custom rule type
        const customRuleType = customRuleTypes.find(rt => rt.value === card.ruleType);
        if (customRuleType) {
          return (
            <>
              {/* Pattern field */}
              {customRuleType.hasPattern && (
                <div className="form-group">
                  <label>Biểu thức chính quy (Pattern) <span className="required">*</span></label>
                  <input
                    type="text"
                    value={card.config.pattern || ''}
                    onChange={(e) => updateCardConfig({ pattern: e.target.value })}
                    placeholder="VD: ^[0-9]{16}$ (16 chữ số)"
                    style={{fontFamily: 'monospace'}}
                  />
                  <small>
                    Ví dụ: <code>^[0-9]+$</code> (chỉ số), <code>^[A-Z][a-z]+$</code> (chữ hoa đầu)
                  </small>
                </div>
              )}

              {/* Range fields */}
              {customRuleType.hasRange && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Giá trị tối thiểu</label>
                    <input
                      type="number"
                      value={card.config.min || ''}
                      onChange={(e) => updateCardConfig({ min: parseFloat(e.target.value) || undefined })}
                      placeholder="VD: 0"
                    />
                  </div>
                  <div className="form-group">
                    <label>Giá trị tối đa</label>
                    <input
                      type="number"
                      value={card.config.max || ''}
                      onChange={(e) => updateCardConfig({ max: parseFloat(e.target.value) || undefined })}
                      placeholder="VD: 1000000"
                    />
                  </div>
                </div>
              )}

              {/* Length fields */}
              {customRuleType.hasLength && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Độ dài tối thiểu</label>
                    <input
                      type="number"
                      value={card.config.minLength || ''}
                      onChange={(e) => updateCardConfig({ minLength: parseInt(e.target.value) || undefined })}
                      placeholder="VD: 3"
                    />
                  </div>
                  <div className="form-group">
                    <label>Độ dài tối đa</label>
                    <input
                      type="number"
                      value={card.config.maxLength || ''}
                      onChange={(e) => updateCardConfig({ maxLength: parseInt(e.target.value) || undefined })}
                      placeholder="VD: 100"
                    />
                  </div>
                </div>
              )}

              {/* Custom field */}
              {customRuleType.hasCustom && (
                <div className="form-group">
                  <label>Cấu hình tùy chỉnh</label>
                  <textarea
                    value={card.config.customConfig || ''}
                    onChange={(e) => updateCardConfig({ customConfig: e.target.value })}
                    placeholder="Nhập cấu hình bổ sung (JSON hoặc text)"
                    rows={3}
                    style={{fontFamily: 'monospace', fontSize: '0.875rem'}}
                  />
                </div>
              )}

              {/* Message field - always show for custom rules */}
              <div className="form-group">
                <label>Thông báo lỗi</label>
                <input
                  type="text"
                  value={card.config.message || ''}
                  onChange={(e) => updateCardConfig({ message: e.target.value })}
                  placeholder="VD: Giá trị không hợp lệ"
                />
              </div>
            </>
          );
        }
        return null;
    }
  };

  // Group rules by entity
  const groupedRules = rules.reduce((acc, rule) => {
    if (!acc[rule.entityName]) {
      acc[rule.entityName] = [];
    }
    acc[rule.entityName].push(rule);
    return acc;
  }, {});

  return (
    <div className="validation-rules">
      <div className="header">
        <h2><Shield size={28} /> Validation Rules</h2>
        <button className="btn btn-primary" onClick={() => handleOpenModal()}>
          <Plus size={18} /> Thêm Rule
        </button>
      </div>

      <div className="rules-list">
        {rules.length === 0 ? (
          <div className="empty-state">
            <Shield size={64} />
            <h3>Chưa có quy tắc validation</h3>
            <p>Tạo các quy tắc để kiểm tra tính hợp lệ của dữ liệu</p>
            <button className="btn btn-primary" onClick={() => handleOpenModal()}>
              <Plus size={18} /> Thêm quy tắc đầu tiên
            </button>
          </div>
        ) : (
          <div style={{display: 'flex', flexDirection: 'column', gap: '2rem'}}>
            {Object.entries(groupedRules).map(([entityName, entityRules]) => (
              <div key={entityName}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '1rem',
                  paddingBottom: '0.75rem',
                  borderBottom: '2px solid var(--border)'
                }}>
                  <Shield size={24} style={{color: 'var(--ey-yellow)', background: 'var(--ey-black)', padding: '0.5rem', borderRadius: 'var(--radius)'}} />
                  <h3 style={{margin: 0, fontSize: '1.25rem', fontWeight: 700}}>{entityName}</h3>
                  <span className="badge badge-gray">{entityRules.length} rules</span>
                </div>

                <div className="cards-grid">
                  {entityRules.map(rule => (
                    <div key={rule.id} className={`card ${!rule.enabled ? 'disabled' : ''}`} style={{opacity: rule.enabled ? 1 : 0.6}}>
                      <div className="card-header">
                        <div className="card-title">
                          <h3>{rule.ruleName || 'Unnamed Rule'}</h3>
                          <p>
                            <span className={`badge ${getRuleTypeBadge(rule.ruleType)}`}>
                              {getRuleTypeLabel(rule.ruleType)}
                            </span>
                            {' • '}{rule.fieldName}
                          </p>
                        </div>
                        <div className="card-actions">
                          <button
                            className="btn-icon"
                            onClick={() => handleDuplicate(rule)}
                            title="Nhân bản"
                          >
                            <Copy size={16} />
                          </button>
                          <button 
                            className="btn-icon" 
                            onClick={() => handleOpenModal(rule)}
                            title="Sửa"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button 
                            className="btn-icon btn-danger" 
                            onClick={() => handleDelete(rule.id)}
                            title="Xóa"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      <div className="card-body">
                        <div className="card-body-item">
                          <label>Trạng thái</label>
                          <value>
                            <button
                              className={`btn-toggle ${rule.enabled ? 'active' : ''}`}
                              onClick={() => handleToggleEnabled(rule)}
                            >
                              {rule.enabled ? (
                                <><CheckCircle size={16} /> Đang bật</>
                              ) : (
                                <><AlertCircle size={16} /> Đã tắt</>
                              )}
                            </button>
                          </value>
                        </div>

                        <div className="card-body-item">
                          <label>Cấu hình</label>
                          <value style={{fontSize: '0.875rem', wordBreak: 'break-word'}}>
                            {rule.ruleType === 'range' && (
                              <span>
                                {rule.config.min !== undefined && `Min: ${rule.config.min}`}
                                {rule.config.min !== undefined && rule.config.max !== undefined && ' • '}
                                {rule.config.max !== undefined && `Max: ${rule.config.max}`}
                              </span>
                            )}
                            {rule.ruleType === 'regex' && (
                              <code style={{fontSize: '0.8125rem'}}>{rule.config.pattern}</code>
                            )}
                            {rule.ruleType === 'length' && (
                              <span>
                                {rule.config.min !== undefined && `Min: ${rule.config.min}`}
                                {rule.config.min !== undefined && rule.config.max !== undefined && ' • '}
                                {rule.config.max !== undefined && `Max: ${rule.config.max}`}
                              </span>
                            )}
                            {rule.ruleType === 'dataType' && (
                              <span className="badge badge-info">{rule.config.type}</span>
                            )}
                            {rule.ruleType === 'custom' && (
                              <span className="badge badge-gray">Custom JS Code</span>
                            )}
                            {rule.ruleType === 'required' && (
                              <span className="badge badge-danger">Required Field</span>
                            )}
                          </value>
                        </div>

                        <div className="card-body-item">
                          <label>Thông báo lỗi</label>
                          <value style={{fontSize: '0.875rem', color: 'var(--danger)', fontStyle: 'italic'}}>
                            "{rule.config.message || 'Không có thông báo'}"
                          </value>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <Shield size={24} />
                {editingRule ? 'Sửa Validation Rule' : 'Thêm Validation Rule'}
              </h3>
              <button className="btn-close" onClick={handleCloseModal}>×</button>
            </div>

            <div className="modal-body">
              {/* Template Selection */}
              {!editingRule && (
                <div style={{
                  padding: '1rem',
                  background: 'linear-gradient(135deg, rgba(255,230,0,0.1) 0%, rgba(255,230,0,0.05) 100%)',
                  borderRadius: 'var(--radius)',
                  border: '1px solid rgba(255,230,0,0.3)',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem'}}>
                    <Zap size={18} style={{color: 'var(--ey-black)'}} />
                    <strong style={{color: 'var(--ey-black)'}}>Templates nhanh</strong>
                  </div>
                  <div className="form-group" style={{marginBottom: 0}}>
                    <select
                      value={selectedTemplate}
                      onChange={(e) => handleTemplateSelect(e.target.value)}
                    >
                      <option value="">-- Chọn template có sẵn --</option>
                      <option value="email">Email format</option>
                      <option value="phone">Số điện thoại VN</option>
                      <option value="cccd">CMND/CCCD</option>
                      <option value="positiveNumber">Số dương (>0)</option>
                      <option value="age18Plus">Tuổi >= 18</option>
                    </select>
                    <small>Chọn template để tự động điền cấu hình</small>
                  </div>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Đối tượng <span className="required">*</span></label>
                  <select
                    value={formData.entityId}
                    onChange={(e) => setFormData({ ...formData, entityId: e.target.value, fieldId: '' })}
                  >
                    <option value="">-- Chọn đối tượng --</option>
                    {entities.map(entity => (
                      <option key={entity.id} value={entity.id}>
                        {entity.name} ({entity.code})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Split Layout: Fields (Left) + Rules (Right) */}
              {formData.entityId && selectedEntity?.fields && (
                <div className="validation-split-layout">
                  {/* Left Sidebar: Field Selection */}
                  <div className="fields-sidebar">
                    <label style={{marginBottom: '0.75rem', display: 'block', fontWeight: 600, position: 'sticky', top: 0, background: 'white', paddingBottom: '0.5rem', zIndex: 1}}>
                      <Columns size={18} style={{display: 'inline', marginRight: '0.5rem', color: 'var(--ey-yellow)'}} />
                      Trường dữ liệu <span className="required">*</span>
                    </label>
                    <div className="field-selection-list">
                      {selectedEntity.fields.map(field => (
                        <div
                          key={field.id}
                          className={`field-selection-card ${formData.fieldId === field.id ? 'selected' : ''}`}
                          onClick={() => {
                            setFormData({ ...formData, fieldId: field.id });
                            setRuleCards([]);
                            setSelectedRuleType('');
                          }}
                        >
                          <div style={{display: 'flex', alignItems: 'flex-start', gap: '0.75rem'}}>
                            <div style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: 'var(--radius)',
                              background: formData.fieldId === field.id ? 'var(--ey-gradient)' : 'var(--gray-100)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              transition: 'var(--transition)'
                            }}>
                              <Columns size={18} style={{color: formData.fieldId === field.id ? 'var(--ey-black)' : 'var(--text-secondary)'}} />
                            </div>
                            <div style={{flex: 1, minWidth: 0}}>
                              <div style={{fontWeight: 600, fontSize: '0.9375rem', marginBottom: '0.25rem', wordBreak: 'break-word'}}>
                                {field.name}
                              </div>
                              <div style={{fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '0.375rem', fontFamily: 'monospace'}}>
                                {field.code}
                              </div>
                              {field.dataType && (
                                <span className="badge badge-gray" style={{fontSize: '0.75rem'}}>
                                  {field.dataType}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right Panel: Rule Management */}
                  <div className="rules-panel">
                    {!formData.fieldId ? (
                      <div style={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: '1rem',
                        color: 'var(--text-secondary)',
                        textAlign: 'center',
                        padding: '2rem'
                      }}>
                        <Shield size={48} style={{color: 'var(--border)'}} />
                        <div>
                          <div style={{fontWeight: 600, fontSize: '1.125rem', marginBottom: '0.5rem'}}>Chọn trường dữ liệu</div>
                          <div style={{fontSize: '0.875rem'}}>Click vào một trường bên trái để quản lý validation rules</div>
                        </div>
                      </div>
                    ) : (
                      <div style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
                {/* Field Info Header */}
                <div style={{
                  padding: '1rem',
                  background: 'linear-gradient(135deg, rgba(255,230,0,0.1) 0%, rgba(255,230,0,0.05) 100%)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid rgba(255,230,0,0.3)',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{display: 'flex', alignItems: 'center', gap: '0.75rem'}}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: 'var(--radius)',
                      background: 'var(--ey-gradient)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Columns size={20} style={{color: 'var(--ey-black)'}} />
                    </div>
                    <div style={{flex: 1}}>
                      <div style={{fontWeight: 700, fontSize: '1.125rem', marginBottom: '0.25rem'}}>
                        {selectedField?.name}
                      </div>
                      <div style={{fontSize: '0.875rem', color: 'var(--text-secondary)', fontFamily: 'monospace'}}>
                        {selectedField?.code} {selectedField?.dataType && `• ${selectedField.dataType}`}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Existing Rules */}
                {existingFieldRules.length > 0 && (
                  <div style={{marginBottom: '1.5rem'}}>
                    <h4 style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem'}}>
                      <CheckCircle size={18} style={{color: 'var(--success)'}} />
                      Rules đang áp dụng ({existingFieldRules.length})
                    </h4>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                      {existingFieldRules.map(rule => (
                        <div key={rule.id} className="rule-card" style={{borderColor: rule.enabled ? 'var(--border)' : 'var(--border-light)', opacity: rule.enabled ? 1 : 0.6}}>
                          <div className="rule-card-header">
                            <div style={{flex: 1}}>
                              <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem'}}>
                                <span className={`badge ${getRuleTypeBadge(rule.ruleType)}`}>
                                  {getRuleTypeLabel(rule.ruleType)}
                                </span>
                                {rule.ruleName && (
                                  <span style={{fontSize: '0.875rem', fontWeight: 600}}>
                                    {rule.ruleName}
                                  </span>
                                )}
                              </div>
                              <div style={{fontSize: '0.8125rem', color: 'var(--text-secondary)'}}>
                                {rule.config.message || 'Không có thông báo lỗi'}
                              </div>
                            </div>
                            <div style={{display: 'flex', gap: '0.5rem'}}>
                              <button
                                className="btn-icon"
                                onClick={() => {
                                  setEditingRule(rule);
                                  setFormData({
                                    entityId: rule.entityId,
                                    fieldId: rule.fieldId
                                  });
                                  setRuleCards([{
                                    id: Date.now(),
                                    ruleType: rule.ruleType,
                                    ruleName: rule.ruleName || '',
                                    config: rule.config
                                  }]);
                                  setIsModalOpen(true);
                                }}
                                title="Sửa rule"
                              >
                                <Edit2 size={16} />
                              </button>
                              <button
                                className="btn-icon btn-danger"
                                onClick={() => {
                                  if (window.confirm(`Xóa rule "${rule.ruleName || getRuleTypeLabel(rule.ruleType)}"?`)) {
                                    storageService.deleteValidationRule(rule.id);
                                    loadRules();
                                  }
                                }}
                                title="Xóa rule"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add New Rules Section */}
                <div style={{marginTop: 'auto'}}>
                <h4 style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem'}}>
                  <Plus size={18} style={{color: 'var(--ey-yellow)'}} />
                  Thêm Rules mới
                </h4>

                {/* Rule Cards Display */}
                {ruleCards.length > 0 && (
                  <div style={{display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem'}}>
                    {ruleCards.map(card => (
                      <div key={card.id} className="rule-card">
                        <div className="rule-card-header">
                          <div style={{flex: 1}}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem'}}>
                              <span className={`badge ${getRuleTypeBadge(card.ruleType)}`}>
                                {getRuleTypeLabel(card.ruleType)}
                              </span>
                              <input
                                type="text"
                                value={card.ruleName}
                                onChange={(e) => handleUpdateRuleCard(card.id, { ruleName: e.target.value })}
                                placeholder="Tên rule (tùy chọn)"
                                style={{
                                  flex: 1,
                                  border: '1px solid var(--border-light)',
                                  borderRadius: 'var(--radius-sm)',
                                  padding: '0.25rem 0.5rem',
                                  fontSize: '0.875rem'
                                }}
                              />
                            </div>
                          </div>
                          <button
                            className="btn-icon btn-danger"
                            onClick={() => handleRemoveRuleCard(card.id)}
                            title="Xóa rule"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <div className="rule-card-body">
                          {renderRuleCardConfig(card)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Rule Interface */}
                <div style={{
                  padding: '1.25rem',
                  background: 'var(--gray-50)',
                  borderRadius: 'var(--radius-lg)',
                  border: '2px dashed var(--border)'
                }}>
                  <div className="form-row" style={{marginBottom: 0}}>
                    <div className="form-group" style={{marginBottom: 0}}>
                      <label>Chọn loại rule</label>
                      <select
                        value={selectedRuleType}
                        onChange={(e) => setSelectedRuleType(e.target.value)}
                      >
                        <option value="">-- Chọn loại rule --</option>
                        <optgroup label="Loại rule mặc định">
                          <option value="required">✓ Bắt buộc (Required)</option>
                          <option value="dataType">🔤 Kiểu dữ liệu (Data Type)</option>
                          <option value="range">📊 Khoảng giá trị (Range)</option>
                          <option value="regex">🔍 Định dạng (Regex)</option>
                          <option value="length">📏 Độ dài (Length)</option>
                          <option value="custom">⚙️ Tùy chỉnh (Custom)</option>
                        </optgroup>
                        {customRuleTypes.length > 0 && (
                          <optgroup label="Loại rule tùy chỉnh">
                            {customRuleTypes.map(rt => (
                              <option key={rt.id} value={rt.value}>
                                ⚡ {rt.name}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </div>
                    <div style={{display: 'flex', alignItems: 'flex-end'}}>
                      <button
                        className="btn btn-primary"
                        onClick={handleAddRuleCard}
                        disabled={!selectedRuleType}
                      >
                        <Plus size={16} /> Add Rule
                      </button>
                    </div>
                  </div>
                </div>
                </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseModal}>
                Hủy
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleSave}
                disabled={
                  !formData.entityId || 
                  !formData.fieldId ||
                  ruleCards.length === 0
                }
              >
                {editingRule ? 'Cập nhật' : `Lưu ${ruleCards.length} Rule(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ValidationRules;
